import { defineStore } from "pinia";
import accountService from "@/services/account.service";
import { useFavouritesStore } from "@/stores/favourites.store";
import type {
  LoginCredentials,
  RegisterData,
  User,
} from "@/services/account.service";

function loadStoredUser(): User | null {
  const raw = localStorage.getItem("authUser");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: loadStoredUser(),
    token: localStorage.getItem("authToken"),

    isLoggingIn: false,
    loginError: "",

    isRegistering: false,
    registerError: "",

    isVerifyingOtp: false,
    verifyOtpError: "",
    verifyOtpSuccess: "",

    isResendingOtp: false,
    resendOtpError: "",
    resendOtpSuccess: "",

    isFetchingProfile: false,

    isUpdatingProfile: false,
    updateProfileError: "",
    updateProfileSuccess: "",

    isChangingPassword: false,
    changePasswordError: "",
    changePasswordSuccess: "",

    isSendingResetLink: false,
    forgotPasswordError: "",
    forgotPasswordSuccess: "",

    isValidatingResetOtp: false,
    validateResetOtpError: "",

    isResettingPassword: false,
    resetPasswordError: "",
    resetPasswordSuccess: "",

    isDeletingAccount: false,
    deleteAccountError: "",
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
  },

  actions: {
    persistSession(token: string, user: User) {
      this.token = token;
      this.user = user;
      localStorage.setItem("authToken", token);
      localStorage.setItem("authUser", JSON.stringify(user));
    },

    clearSession() {
      this.token = null;
      this.user = null;
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      useFavouritesStore().clearFavourites();
    },

    async login(credentials: LoginCredentials) {
      this.isLoggingIn = true;
      this.loginError = "";

      try {
        const response = await accountService.login(credentials);
        const { user, token } = response.data;
        this.persistSession(token, user);
        return response.data;
      } catch (error: any) {
        this.loginError =
          error.message || "Unable to log in. Please try again.";
        throw error;
      } finally {
        this.isLoggingIn = false;
      }
    },

    async register(data: RegisterData) {
      this.isRegistering = true;
      this.registerError = "";

      try {
        const response = await accountService.register(data);
        // Registration only issues an OTP - the caller redirects to the
        // verify-otp page and the session is created once it's confirmed.
        return response.data;
      } catch (error: any) {
        this.registerError =
          error.message || "Unable to create your account. Please try again.";
        throw error;
      } finally {
        this.isRegistering = false;
      }
    },

    async verifyOtp(otpId: string, otp: string) {
      this.isVerifyingOtp = true;
      this.verifyOtpError = "";
      this.verifyOtpSuccess = "";

      try {
        const response = await accountService.verifyOtp({ otpId, otp });
        // Verification just confirms the account - no session is issued,
        // the user still needs to log in afterwards.
        this.verifyOtpSuccess =
          response.message || "Your account has been verified.";
        return response;
      } catch (error: any) {
        this.verifyOtpError =
          error.message || "Invalid or expired code. Please try again.";
        throw error;
      } finally {
        this.isVerifyingOtp = false;
      }
    },

    async resendOtp(otpId: string) {
      this.isResendingOtp = true;
      this.resendOtpError = "";
      this.resendOtpSuccess = "";

      try {
        const response = await accountService.resendOtp(otpId);
        this.resendOtpSuccess =
          response.message || "A new code has been sent to your email.";
        return response.data;
      } catch (error: any) {
        this.resendOtpError =
          error.message || "Unable to resend code. Please try again.";
        throw error;
      } finally {
        this.isResendingOtp = false;
      }
    },

    async logout() {
      try {
        await accountService.logout();
      } catch {
        // Best-effort: still clear the local session even if the API call fails
      } finally {
        this.clearSession();
      }
    },

    async fetchProfile() {
      this.isFetchingProfile = true;

      try {
        const response = await accountService.getProfile();
        const user = response.data;
        this.user = user;
        localStorage.setItem("authUser", JSON.stringify(user));
        return user;
      } finally {
        this.isFetchingProfile = false;
      }
    },

    async initializeAuth() {
      if (!this.token) return;

      try {
        await this.fetchProfile();
      } catch (error: any) {
        // Only a confirmed-invalid session (401) should log the user out.
        // Network errors, timeouts, or a flaky server shouldn't destroy a
        // valid session on refresh - the cached user stays put and we'll
        // retry fetching the profile next time.
        if (error?.statusCode === 401) {
          this.clearSession();
        }
      }
    },

    async updateProfile(data: Partial<User>) {
      this.isUpdatingProfile = true;
      this.updateProfileError = "";
      this.updateProfileSuccess = "";

      try {
        const response = await accountService.updateProfile(data);
        // This endpoint returns `data: null` on success rather than echoing
        // back the updated user, so merge what we sent (and anything the API
        // does return) into the existing user instead of overwriting it.
        if (this.user) {
          this.user = { ...this.user, ...data, ...response.data };
        } else if (response.data) {
          this.user = response.data;
        }
        localStorage.setItem("authUser", JSON.stringify(this.user));
        this.updateProfileSuccess =
          response.message || "Profile updated successfully.";
        return this.user;
      } catch (error: any) {
        this.updateProfileError =
          error.message || "Unable to update your profile. Please try again.";
        throw error;
      } finally {
        this.isUpdatingProfile = false;
      }
    },

    async changePassword(
      oldPassword: string,
      password: string,
      confirmPassword: string
    ) {
      this.isChangingPassword = true;
      this.changePasswordError = "";
      this.changePasswordSuccess = "";

      try {
        const response = await accountService.changePassword(
          oldPassword,
          password,
          confirmPassword
        );
        this.changePasswordSuccess =
          response.message || "Password changed successfully.";
        return response;
      } catch (error: any) {
        this.changePasswordError =
          error.message || "Unable to change your password. Please try again.";
        throw error;
      } finally {
        this.isChangingPassword = false;
      }
    },

    async forgotPassword(email: string) {
      this.isSendingResetLink = true;
      this.forgotPasswordError = "";
      this.forgotPasswordSuccess = "";

      try {
        const response = await accountService.forgotPassword(email);
        this.forgotPasswordSuccess =
          response.message ||
          "A password reset code has been sent to your email.";
        return response.data;
      } catch (error: any) {
        this.forgotPasswordError =
          error.message || "Unable to send reset code. Please try again.";
        throw error;
      } finally {
        this.isSendingResetLink = false;
      }
    },

    async validateResetPasswordOtp(otpId: string, otp: string) {
      this.isValidatingResetOtp = true;
      this.validateResetOtpError = "";

      try {
        const response = await accountService.validateResetPasswordOtp({
          otpId,
          otp,
        });
        return response.data;
      } catch (error: any) {
        this.validateResetOtpError =
          error.message || "Invalid or expired code. Please try again.";
        throw error;
      } finally {
        this.isValidatingResetOtp = false;
      }
    },

    async resetPassword(
      userId: string,
      token: string,
      password: string,
      confirmPassword: string
    ) {
      this.isResettingPassword = true;
      this.resetPasswordError = "";
      this.resetPasswordSuccess = "";

      try {
        const response = await accountService.resetPassword({
          userId,
          token,
          password,
          confirmPassword,
        });
        this.resetPasswordSuccess =
          response.message || "Your password has been reset successfully.";
        return response;
      } catch (error: any) {
        this.resetPasswordError =
          error.message || "Unable to reset your password. Please try again.";
        throw error;
      } finally {
        this.isResettingPassword = false;
      }
    },

    async deleteAccount(password: string) {
      this.isDeletingAccount = true;
      this.deleteAccountError = "";

      try {
        const response = await accountService.deleteAccount(password);
        this.clearSession();
        return response;
      } catch (error: any) {
        this.deleteAccountError =
          error.message || "Unable to delete your account. Please try again.";
        throw error;
      } finally {
        this.isDeletingAccount = false;
      }
    },

    clearMessages() {
      this.loginError = "";
      this.registerError = "";
      this.verifyOtpError = "";
      this.verifyOtpSuccess = "";
      this.resendOtpError = "";
      this.resendOtpSuccess = "";
      this.updateProfileError = "";
      this.updateProfileSuccess = "";
      this.changePasswordError = "";
      this.changePasswordSuccess = "";
      this.forgotPasswordError = "";
      this.forgotPasswordSuccess = "";
      this.validateResetOtpError = "";
      this.resetPasswordError = "";
      this.resetPasswordSuccess = "";
      this.deleteAccountError = "";
    },
  },
});

export default useAuthStore;
